import React, { ReactNode } from 'react'
import { Box, Button } from '@mui/material'
import CustomTooltip from 'src/designSystem/CustomTooltip'

const chipSize = 36
const chipBorderWidth = 1
const countSize = 20
const countOverhang = 8
const countRingWidth = 1

type Props = {
  label: string
  icon: ReactNode
  count: number
  selected: boolean
  expanded: boolean
  onClick: () => void
}

const PriorityFilterChip = ({ label, icon, count, selected, expanded, onClick }: Props) => {
  const description = count > 0 ? `${label}, ${count}` : label

  return (
    <CustomTooltip title={label}>
      <Button
        aria-label={description}
        aria-pressed={selected}
        color="inherit"
        onClick={onClick}
        sx={(theme) => ({
          backgroundColor: selected ? theme.palette.primary.main : theme.palette.common.white,
          border: `${chipBorderWidth}px solid ${selected ? theme.palette.primary.main : theme.palette.borderGrey}`,
          borderRadius: `${chipSize}px`,
          color: selected ? theme.palette.primary.contrastText : theme.palette.darkestGreen,
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 'normal',
          gap: 0.5,
          height: chipSize,
          lineHeight: '16px',
          minWidth: 0,
          overflow: 'visible',
          padding: expanded ? theme.spacing(1, 2) : 0,
          position: 'relative',
          textTransform: 'none',
          width: expanded ? undefined : chipSize,
          '&:hover': {
            backgroundColor: selected ? theme.palette.primary.dark : theme.palette.lightestGrey,
          },
        })}
      >
        {icon}
        {expanded && (
          <Box component="span" paddingRight="2px">
            {label}
          </Box>
        )}
        {count > 0 && (
          <Box
            component="span"
            data-testid={`${label}-count`}
            sx={(theme) => ({
              backgroundColor: theme.palette.graySubtle,
              borderRadius: `${countSize / 2}px`,
              boxShadow: `0 0 0 ${countRingWidth}px ${theme.palette.common.white}`,
              boxSizing: 'border-box',
              color: theme.palette.darkestGreen,
              fontWeight: theme.typography.fontWeightBold,
              lineHeight: '19px',
              minWidth: countSize,
              paddingBottom: '1px',
              paddingLeft: '6px',
              paddingRight: '6px',
              position: 'absolute',
              right: -(countOverhang + chipBorderWidth),
              textAlign: 'center',
              top: -(countOverhang + chipBorderWidth),
            })}
          >
            {count}
          </Box>
        )}
      </Button>
    </CustomTooltip>
  )
}

export default PriorityFilterChip
